import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { searchDvhcApi } from '../services/trungTamApi';
import type { DvhcItem } from '../types';

type DropdownRect = { top: number; left: number; width: number };

type Props = {
  value: string;
  displayText: string;
  onChange: (maDvhc: string, tenDayDu: string) => void;
  disabled?: boolean;
  placeholder?: string;
};

const DvhcAutocomplete: React.FC<Props> = ({ value, displayText, onChange, disabled, placeholder = 'Nhập tên xã/phường/huyện...' }) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<DvhcItem[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const [dropdownRect, setDropdownRect] = useState<DropdownRect | null>(null);

  const doSearch = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await searchDvhcApi(q, 20);
      if (res.EC === 0) setOptions(res.DT || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setQuery(v);
    setOpen(true);
    setActiveIndex(-1);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(v), 300);
  };

  const handleSelect = (item: DvhcItem) => {
    onChange(item.maDvhc, item.tenDayDu);
    setQuery('');
    setOpen(false);
    setOptions([]);
  };

  const handleClear = () => {
    onChange('', '');
    setQuery('');
    setOptions([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || options.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => Math.min(prev + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(options[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  // Close dropdown on outside click (portal-aware: ignore clicks inside dropdown)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const showDropdown = open && (options.length > 0 || loading);

  // Track input position so the portal-rendered dropdown follows it on
  // scroll / resize / layout changes.
  useLayoutEffect(() => {
    if (!showDropdown) return;
    const updateRect = () => {
      const el = containerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setDropdownRect({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [showDropdown, options.length]);

  return (
    <div className="dvhc-autocomplete" ref={containerRef}>
      {value && displayText ? (
        <div className="dvhc-autocomplete__selected">
          <div className="dvhc-autocomplete__selected-info">
            <span className="dvhc-autocomplete__selected-name">{displayText}</span>
            <span className="dvhc-autocomplete__selected-code">{value}</span>
          </div>
          {!disabled && (
            <button
              type="button"
              className="dvhc-autocomplete__clear"
              onClick={handleClear}
              title="Xóa"
            >
              <i className="material-icons">close</i>
            </button>
          )}
        </div>
      ) : (
        <div className="dvhc-autocomplete__input-wrap">
          <i className="material-icons dvhc-autocomplete__icon">location_on</i>
          <input
            ref={inputRef}
            type="text"
            className="dvhc-autocomplete__input"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => { if (options.length > 0) setOpen(true); }}
            placeholder={placeholder}
            disabled={disabled}
          />
          {loading && <span className="tt-spinner dvhc-autocomplete__spinner" />}
        </div>
      )}

      {showDropdown && dropdownRect && createPortal(
        <ul
          ref={dropdownRef}
          className="dvhc-autocomplete__dropdown dvhc-autocomplete__dropdown--portal"
          style={{
            position: 'fixed',
            top: dropdownRect.top,
            left: dropdownRect.left,
            width: dropdownRect.width,
          }}
        >
          {loading && options.length === 0 && (
            <li className="dvhc-autocomplete__loading">
              <span className="tt-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
              Đang tìm...
            </li>
          )}
          {options.map((item, idx) => (
            <li
              key={item.maDvhc}
              className={`dvhc-autocomplete__option${idx === activeIndex ? ' dvhc-autocomplete__option--active' : ''}`}
              onMouseEnter={() => setActiveIndex(idx)}
              onClick={() => handleSelect(item)}
            >
              <span className="dvhc-autocomplete__option-name">{item.tenDayDu}</span>
              <span className="dvhc-autocomplete__option-code">{item.maDvhc}</span>
            </li>
          ))}
        </ul>,
        document.body,
      )}
    </div>
  );
};

export default DvhcAutocomplete;
